import { useEffect, useState } from 'react';
import {
  isPreviewTemplate,
  parseAnimation,
  type AnimationConfig,
  type PreviewTemplate,
} from '@game-platform/pack-animation';
import { operatorConsole, type OperatorMe } from '../../lib/api/operatorConsole';

/** What a console user may do with pack openings, from /me. */
export interface OpeningAccess {
  /** The operator may set openings at all (allow_animation_settings). */
  allowed: boolean;
  /** Themes access: reads the global animation and opens the Pack Opening page. */
  canEditGlobal: boolean;
  /** The template players get by default: its global animation is what a pack inherits. */
  template: PreviewTemplate;
}

export function openingAccessOf(me: OperatorMe): OpeningAccess {
  const template = me.operator.default_template;
  return {
    allowed: me.operator.allow_animation_settings ?? false,
    canEditGlobal: me.owner.permissions?.includes('themes') ?? false,
    template: isPreviewTemplate(template) ? template : 'classic',
  };
}

export interface GlobalAnimation {
  animation: AnimationConfig;
  designSystem: Record<string, unknown>;
}

// The default template's animation and theme; null when it is not readable or the read failed.
export function useGlobalAnimation(access: OpeningAccess | null) {
  const readable = Boolean(access?.allowed && access.canEditGlobal);
  const template = access?.template ?? 'classic';
  const [read, setRead] = useState<{ template: string; global: GlobalAnimation | null } | null>(null);

  useEffect(() => {
    if (!readable) return;
    let cancelled = false;
    operatorConsole.getTemplateTheme(template).then(
      (theme) => {
        if (cancelled) return;
        const designSystem = theme.design_system;
        setRead({ template, global: { animation: parseAnimation(designSystem['animation']), designSystem } });
      },
      () => {
        if (!cancelled) setRead({ template, global: null });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [readable, template]);

  const current = readable && read?.template === template ? read : null;
  return { global: current?.global ?? null, loading: readable && !current };
}
